import React, { useState } from "react";
import { css } from "@emotion/react";

import { WrapperComponent } from "../_shared-components/arrangement/WrapperComponent";
import { ImageGalleryRow } from "./ImageGalleryRow";
import { SwarovskiButton } from "../_shared-components/ui-elements/SwarovskiButton";
import { GalleryModal } from "./GalleryModal";
import { CSS_COLORS, CSS_SPACER_SIZES } from "../../styles/styles-css-variables";
import { ContentfulComponentCMSHook } from "@swo/shared";
import { getComponentCMSClassname } from "../cms/edit-overlay/component-hook-class";
import { useBodyOverflowStyles } from "../../hooks/use-body-overflow-styles-for";
import { ImageGalleryType, type TranslatedValue } from "@swo/storefront";
import { CSS_MQ } from "../../styles/breakpoints";
import { T } from "../T";

export function ImageGalleryComponent(
  props: ImageGalleryType & { loadMoreLabel: TranslatedValue } & ContentfulComponentCMSHook,
) {
  const { rowsCollection, galleryLayout } = props;

  const [maxDisplayedRows, setMaxDisplayedRows] = useState(3);
  const [modal, setModal] = useState<null | { rowIndex: number; indexInRow: number }>(null);

  const width = galleryLayout === "full-width" ? "full" : "content";

  function loadMoreRows() {
    setMaxDisplayedRows(r => r + 3);
  }

  const canLoadMore = maxDisplayedRows < rowsCollection.length;

  const closeModal = () => setModal(null);

  useBodyOverflowStyles(!!modal);

  function onClick(rowIndex: number) {
    return (indexInRow: number) => {
      return setModal({
        rowIndex,
        indexInRow,
      });
    };
  }

  const visibleRows = rowsCollection.slice(0, maxDisplayedRows);

  return (
    <WrapperComponent width={width}>
      <div
        className={getComponentCMSClassname(props?.sys?.id)}
        css={css`
          position: relative;
          width: 100%;
          cursor: pointer;

          ${CSS_MQ.atMostXs} {
            > * + * {
              margin-top: 5px;
            }
          }
        `}
      >
        {visibleRows.map((r, idx) => (
          <ImageGalleryRow
            imagesCollection={r.imagesCollection}
            key={idx}
            onClick={onClick(idx)}
            width={width}
            isLast={visibleRows.length - 1 === idx}
          />
        ))}
      </div>

      {canLoadMore && (
        <div
          css={css`
            position: relative;
            width: 100%;
            text-align: center;

            color: ${CSS_COLORS.Green};
            margin-top: ${CSS_SPACER_SIZES.md};
          `}
        >
          <SwarovskiButton onClick={loadMoreRows} variant="normal-underline" as="button">
            <T>{props.loadMoreLabel}</T>
          </SwarovskiButton>
        </div>
      )}

      {modal && (
        <GalleryModal
          initiallySelected={modal}
          images={rowsCollection.flatMap((i, rowIndex) => {
            return i.imagesCollection.map((image, indexInRow) => ({ image, rowIndex, indexInRow }));
          })}
          close={closeModal}
        />
      )}
    </WrapperComponent>
  );
}
